/**
 * Web rich-text editor mirroring the mobile /app/mobile/src/RichTextEditor.tsx.
 *
 * Toolbar: H1 (navy), H2 (red), P, B, I, U, bullet list, ordered list,
 * text-align (left / centre / right), undo, redo. No colour picker —
 * H1 is always navy and H2 is always accent red. A live plain-text
 * counter caps the description at `max` characters.
 */
import { useEffect, useMemo } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight,
  Undo, Redo,
} from "lucide-react";

const RTE_STYLE_ID = "middlesex-rte-styles";

function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(RTE_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = RTE_STYLE_ID;
  el.innerHTML = `
    .mc-rte .ProseMirror {
      outline: none;
      min-height: 180px;
      max-height: 320px;
      overflow-y: auto;
      padding: 10px 12px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid hsl(var(--border));
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 15px;
      line-height: 1.5;
      color: hsl(var(--foreground));
    }
    .mc-rte .ProseMirror:focus { border-color: hsl(var(--ring)); }
    .mc-rte .ProseMirror h1 {
      color: hsl(var(--mc-navy)) !important;
      font-size: 24px;
      font-weight: 600;
      margin: 8px 0;
    }
    .mc-rte .ProseMirror h2 {
      color: hsl(var(--mc-red)) !important;
      font-size: 20px;
      font-weight: 600;
      margin: 8px 0 6px;
    }
    .mc-rte .ProseMirror p { color: hsl(var(--foreground)); margin: 0 0 8px; }
    .mc-rte .ProseMirror ul,
    .mc-rte .ProseMirror ol { padding-left: 20px; margin: 0 0 8px; }
    .mc-rte .ProseMirror li p { margin: 0; }
    .mc-rte .ProseMirror u { text-decoration: underline; }
    .mc-rte .ProseMirror.is-editor-empty:first-child::before {
      color: hsl(var(--muted-foreground));
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
    .mc-rte .ProseMirror [style*="text-align: center"] { text-align: center; }
    .mc-rte .ProseMirror [style*="text-align: right"]  { text-align: right; }
  `;
  document.head.appendChild(el);
}

function Btn({ onClick, active, disabled, label, icon: Icon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={[
        "h-8 min-w-[32px] px-2 rounded-md border text-xs inline-flex items-center justify-center transition-colors",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-white text-primary border-border hover:bg-muted/40",
        disabled ? "opacity-40 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {Icon ? <Icon size={14} /> : <span className="font-semibold">{label}</span>}
    </button>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  max = 1000,
  placeholder,
  testId,
  onReady,
}) {
  useMemo(() => ensureStyles(), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] }, codeBlock: false }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "data-placeholder": placeholder || "Write your description. Use the toolbar to format text.",
        "data-testid": testId,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const plain = ed.getText();
      // Cap by plain-text length so tag noise can't blow past `max`.
      if (plain.length > max) return;
      onChange && onChange(html);
    },
  });

  // Expose a small controller to the parent (used for cursor-position
  // token insertion). Fires once after the editor becomes available.
  useEffect(() => {
    if (!editor || !onReady) return;
    onReady({
      insert: (text) => {
        if (!text) return;
        editor.chain().focus().insertContent(String(text)).run();
      },
      focus: () => editor.chain().focus().run(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Hydrate when parent replaces `value` (edit-mode prefill).
  useEffect(() => {
    if (!editor) return;
    if (value === undefined) return;
    if (editor.getHTML() === (value || "")) return;
    setTimeout(() => {
      if (editor.isDestroyed) return;
      editor.commands.setContent(value || "", false);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return <div className="text-sm text-muted-foreground">Loading editor…</div>;
  }

  const chars = editor.getText().length;
  const over = chars > max;
  const isActive = (name, attrs) => editor.isActive(name, attrs);

  return (
    <div className="mc-rte">
      <div className="flex flex-wrap items-center gap-1 mb-2" data-testid={testId ? `${testId}-toolbar` : undefined}>
        <Btn label="H1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={isActive("heading", { level: 1 })} testId="rt-h1" />
        <Btn label="H2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={isActive("heading", { level: 2 })} testId="rt-h2" />
        <Btn label="P"  onClick={() => editor.chain().focus().setParagraph().run()} active={isActive("paragraph") && !isActive("heading")} testId="rt-p" />
        <span className="w-px h-6 bg-border mx-1" />
        <Btn label="Bold"      icon={BoldIcon}      onClick={() => editor.chain().focus().toggleBold().run()}      active={isActive("bold")}      testId="rt-b" />
        <Btn label="Italic"    icon={ItalicIcon}    onClick={() => editor.chain().focus().toggleItalic().run()}    active={isActive("italic")}    testId="rt-i" />
        <Btn label="Underline" icon={UnderlineIcon} onClick={() => editor.chain().focus().toggleUnderline().run()} active={isActive("underline")} testId="rt-u" />
        <span className="w-px h-6 bg-border mx-1" />
        <Btn label="Bullet list"    icon={List}        onClick={() => editor.chain().focus().toggleBulletList().run()}   active={isActive("bulletList")}   testId="rt-ul" />
        <Btn label="Numbered list"  icon={ListOrdered} onClick={() => editor.chain().focus().toggleOrderedList().run()} active={isActive("orderedList")} testId="rt-ol" />
        <span className="w-px h-6 bg-border mx-1" />
        <Btn label="Align left"   icon={AlignLeft}   onClick={() => editor.chain().focus().setTextAlign("left").run()}   active={editor.isActive({ textAlign: "left" }) || (!editor.isActive({ textAlign: "center" }) && !editor.isActive({ textAlign: "right" }))} testId="rt-align-left" />
        <Btn label="Align centre" icon={AlignCenter} onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} testId="rt-align-center" />
        <Btn label="Align right"  icon={AlignRight}  onClick={() => editor.chain().focus().setTextAlign("right").run()}  active={editor.isActive({ textAlign: "right" })}  testId="rt-align-right" />
        <span className="w-px h-6 bg-border mx-1" />
        <Btn label="Undo" icon={Undo} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} testId="rt-undo" />
        <Btn label="Redo" icon={Redo} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} testId="rt-redo" />
      </div>
      <EditorContent editor={editor} />
      <div className="flex justify-end mt-1">
        <div
          className={`text-xs ${over ? "text-destructive font-semibold" : "text-muted-foreground"}`}
          data-testid={testId ? `${testId}-counter` : undefined}
        >
          {chars}/{max}
        </div>
      </div>
    </div>
  );
}
